/**
 * Side cart drawer (sections/side-cart.liquid).
 *
 * Opens after any add to cart and from elements with [data-side-cart-open].
 * Other scripts can call `document.querySelector('side-cart').refresh({ open: true })`
 * or dispatch `side-cart:open` on document.
 *
 * The theme's minified product form still talks to <cart-drawer> (old hover
 * dropdown, now hidden) and <cart-notification> (old popup). Both instances
 * are redirected here so every add to cart ends in this drawer.
 */
(function () {
  if (customElements.get('side-cart')) return;

  var SECTION_ID = 'side-cart';
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class SideCart extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.side-cart__panel');
      this.lightbox = this.querySelector('[data-side-cart-lightbox]');
      this.lastFocus = null;

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      this.addEventListener('error', this.onImageError.bind(this), true);
      document.addEventListener('click', this.onDocumentClick.bind(this));
      document.addEventListener('side-cart:open', this.refresh.bind(this, { open: true }));

      this.adoptLegacyCart();
    }

    adoptLegacyCart() {
      // Old hover dropdown: product form calls .open() after add; nothing to show now.
      var legacyDrawer = document.querySelector('cart-drawer');
      if (legacyDrawer) legacyDrawer.open = function () {};

      // Old popup: product form calls .renderContents(response) after add.
      var self = this;
      document.querySelectorAll('cart-notification').forEach(function (notification) {
        notification.renderContents = function (response) {
          if (response && response.status) return; // add failed; product form shows the error
          self.refresh({ open: true });
        };
      });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open() {
      if (this.isOpen) return;
      this.lastFocus = document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('side-cart-open');
      this.panel.focus({ preventScroll: true });
    }

    close() {
      if (!this.isOpen) return;
      this.closeLightbox();
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('side-cart-open');
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') this.lastFocus.focus({ preventScroll: true });
    }

    sectionsUrl() {
      return (this.dataset.sectionsUrl || '/') + '?sections=' + SECTION_ID + ',cart-icon-bubble';
    }

    refresh(options) {
      var self = this;
      return fetch(this.sectionsUrl(), { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function (sections) {
          self.render(sections);
          if (options && options.open) self.open();
        });
    }

    render(sections) {
      var html = new DOMParser().parseFromString(sections[SECTION_ID] || '', 'text/html');
      this.querySelectorAll('[data-side-cart-part]').forEach(function (part) {
        var fresh = html.querySelector('[data-side-cart-part="' + part.dataset.sideCartPart + '"]');
        if (fresh) part.innerHTML = fresh.innerHTML;
      });

      var bubble = document.getElementById('cart-icon-bubble');
      var bubbleHtml = sections['cart-icon-bubble'];
      if (bubble && bubbleHtml) {
        var section = new DOMParser().parseFromString(bubbleHtml, 'text/html').querySelector('.shopify-section');
        if (section) bubble.innerHTML = section.innerHTML;
      }
      // Section rendering replaces the app's mounted buttons. Ask Teeinblue to
      // read the latest cart and mount Edit Design on the new line-item nodes.
      document.dispatchEvent(new Event('shopify-event-cart-modified'));
      document.dispatchEvent(new Event('shopify-event-cart-ajax'));
    }

    changeQuantity(item, quantity) {
      var self = this;
      var error = item.querySelector('.side-cart__error');
      item.classList.add('is-loading');
      fetch((window.routes && window.routes.cart_change_url ? window.routes.cart_change_url : '/cart/change') + '.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: item.dataset.key, quantity: quantity }),
      })
        .then(function (res) {
          return res.json().then(function (body) {
            if (!res.ok) throw new Error(body.description || body.message || 'Could not update the cart.');
          });
        })
        .then(function () {
          return self.refresh();
        })
        .catch(function (err) {
          item.classList.remove('is-loading');
          if (error) {
            error.textContent = err.message;
            error.hidden = false;
          }
        });
    }

    // Personalized design images (Teeinblue) are generated a few seconds after the
    // add to cart. Show the product image meanwhile and swap the design in once it exists.
    onImageError(event) {
      var img = event.target;
      if (!img.classList || !img.classList.contains('side-cart__image') || !img.dataset.fallback) return;
      var design = img.getAttribute('src');
      if (design === img.dataset.fallback) return;
      img.src = img.dataset.fallback;

      var attempts = 0;
      (function retry() {
        if (++attempts > 10 || !img.isConnected) return;
        var probe = new Image();
        probe.onload = function () {
          if (img.isConnected) img.src = probe.src;
        };
        probe.onerror = function () {
          setTimeout(retry, 1500);
        };
        probe.src = design + (design.indexOf('?') === -1 ? '?' : '&') + 'retry=' + attempts;
      })();
    }

    openLightbox(src) {
      this.lightbox.querySelector('img').src = src;
      this.lightbox.hidden = false;
      this.lightbox.querySelector('[data-side-cart-lightbox-close]').focus();
    }

    closeLightbox() {
      if (this.lightbox) this.lightbox.hidden = true;
    }

    onClick(event) {
      var target = event.target;
      if (target.closest('[data-side-cart-lightbox-close]')) return this.closeLightbox();
      if (target.closest('[data-side-cart-close]')) return this.close();

      var preview = target.closest('[data-side-cart-preview]');
      if (preview) return this.openLightbox(preview.dataset.sideCartPreview);

      var qty = target.closest('[data-side-cart-qty]');
      if (qty) {
        var item = qty.closest('[data-key]');
        if (item) this.changeQuantity(item, Math.max(0, parseInt(qty.dataset.sideCartQty, 10) || 0));
      }
    }

    onDocumentClick(event) {
      var opener = event.target.closest('[data-side-cart-open]');
      if (!opener) return;
      event.preventDefault();
      this.open();
      this.refresh();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        if (this.lightbox && !this.lightbox.hidden) return this.closeLightbox();
        return this.close();
      }
      if (event.key !== 'Tab') return;
      var focusable = Array.prototype.filter.call(this.panel.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null;
      });
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('side-cart', SideCart);
})();
